import React, { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { createCheckoutSession } from '../../services/api'
import { toast } from 'react-toastify'
import './Pricing.scss'

function Pricing() {
  const { isAuthenticated, loginWithPopup, getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({})

  // Note: Payment success/cancel handling is now done in Dashboard component
  // since Stripe redirects to /dashboard after checkout

  const handleGetStarted = async () => {
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      try {
        const width = 700
        const height = 900
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2
        
        const popup = window.open(
          '',
          'auth0:authorize:popup',
          `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
        )
        
        await loginWithPopup({
          authorizationParams: {
            screen_hint: 'signup'
          }
        }, { popup } as any)
      } catch (error) {
        logger.error('Login error', {
          error: error instanceof Error ? error.message : String(error),
        });
        toast.error('Failed to log in. Please try again.')
      }
    }
  }

  const handleBuyCredits = async (creditPackage: 1 | 5 | 10) => {
    if (!isAuthenticated) {
      handleGetStarted()
      return
    }

    setLoading({ ...loading, [creditPackage]: true })

    try {
      const { url } = await createCheckoutSession(creditPackage, getAccessTokenSilently)
      
      // Redirect to Stripe Checkout
      if (url) {
        window.location.href = url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (error) {
      logger.error('Error creating checkout session', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error('Failed to start checkout. Please try again.')
      setLoading({ ...loading, [creditPackage]: false })
    }
  }

  return (
    <section id="pricing" className="py-5 bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <h2 
              className="text-center mb-2 fw-bold pricing-heading"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '36px',
                lineHeight: '44px',
                letterSpacing: '-0.02em'
              }}
            >
              Simple, Transparent Pricing
            </h2>
            <p 
              className="text-center text-muted mb-5 pricing-subtitle"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '18px',
                lineHeight: '26px'
              }}
            >
              Start free, then pay only for what you need
            </p>

            {/* Free Tier Card */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="bg-white rounded-4 p-4 p-md-5 h-100 shadow-sm border border-2 border-primary position-relative">
                  <div className="position-absolute top-0 end-0 m-3">
                    <span className="badge bg-primary text-uppercase px-3 py-2" style={{ fontSize: '12px' }}>
                      Free Forever
                    </span>
                  </div>
                  <div className="mb-4">
                    <h3 
                      className="fw-bold mb-2"
                      style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '28px',
                        color: '#181a25'
                      }}
                    >
                      The forever-free, no credit card needed plan
                    </h3>
                    <div className="d-flex align-items-baseline mb-3">
                      <span 
                        className="fw-bold me-2"
                        style={{
                          fontSize: '48px',
                          color: '#505fe2',
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                        }}
                      >
                        FREE
                      </span>
                      <span className="text-muted">forever</span>
                    </div>
                  </div>
                  
                  <ul className="list-unstyled mb-4">
                    <li className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2 fw-bold" style={{ fontSize: '20px' }}>✓</span>
                      <span style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#181a25'
                      }}>
                        <strong>3 jobs included</strong> - Generate tailored resume and interview prep plan
                      </span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2 fw-bold" style={{ fontSize: '20px' }}>✓</span>
                      <span style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#181a25'
                      }}>
                        AI-powered job post analysis
                      </span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2 fw-bold" style={{ fontSize: '20px' }}>✓</span>
                      <span style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#181a25'
                      }}>
                        Resume tailoring for each application
                      </span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2 fw-bold" style={{ fontSize: '20px' }}>✓</span>
                      <span style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#181a25'
                      }}>
                        Personalized interview prep plans
                      </span>
                    </li>
                    <li className="mb-3 d-flex align-items-start">
                      <span className="text-primary me-2 fw-bold" style={{ fontSize: '20px' }}>✓</span>
                      <span style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#181a25'
                      }}>
                        Application tracker
                      </span>
                    </li>
                  </ul>

                  <button
                    onClick={handleGetStarted}
                    className="btn btn-primary btn-lg w-100 text-uppercase fw-bold"
                    style={{ padding: '14px' }}
                  >
                    Get Started Free
                  </button>
                </div>
              </div>

              {/* Additional Credits Card */}
              <div className="col-lg-6">
                <div className="bg-white rounded-4 p-4 p-md-5 h-100 shadow-sm border">
                  <div className="mb-4">
                    <h3 
                      className="fw-bold mb-2"
                      style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '28px',
                        color: '#181a25'
                      }}
                    >
                      Need More?
                    </h3>
                    <p 
                      className="text-muted mb-4"
                      style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                        fontSize: '16px',
                        lineHeight: '24px'
                      }}
                    >
                      Buy additional credits when you need them. No subscription required.
                    </p>
                  </div>

                  {/* Single Credit Option */}
                  <div className="border rounded-3 p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h4 
                          className="fw-bold mb-1"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '18px',
                            color: '#181a25'
                          }}
                        >
                          1 Credit
                        </h4>
                        <p 
                          className="text-muted mb-0 small"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '14px'
                          }}
                        >
                          Resume + Interview Prep for 1 job
                        </p>
                      </div>
                      <div className="text-end">
                        <div 
                          className="fw-bold"
                          style={{
                            fontSize: '24px',
                            color: '#181a25',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                          }}
                        >
                          $5 <span style={{ fontSize: '14px' }}>USD</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleBuyCredits(1)}
                      className="btn btn-outline-primary btn-sm w-100"
                      disabled={loading[1]}
                    >
                      {loading[1] ? 'Loading...' : 'Buy 1 Credit'}
                    </button>
                  </div>

                  {/* 5 Credits Option */}
                  <div className="border rounded-3 p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h4 
                          className="fw-bold mb-1"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '18px',
                            color: '#181a25'
                          }}
                        >
                          5 Credits
                        </h4>
                        <p 
                          className="text-muted mb-0 small"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '14px'
                          }}
                        >
                          Resume + Interview Prep for 5 jobs
                        </p>
                        <p 
                          className="text-success mb-0 small fw-bold mt-1"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '12px'
                          }}
                        >
                          Save $5 (20% off)
                        </p>
                      </div>
                      <div className="text-end">
                        <div 
                          className="fw-bold"
                          style={{
                            fontSize: '24px',
                            color: '#181a25',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                          }}
                        >
                          $20 <span style={{ fontSize: '14px' }}>USD</span>
                        </div>
                        <div 
                          className="text-muted small text-decoration-line-through"
                          style={{ fontSize: '14px' }}
                        >
                          $25 <span style={{ fontSize: '10px' }}>USD</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleBuyCredits(5)}
                      className="btn btn-outline-primary btn-sm w-100"
                      disabled={loading[5]}
                    >
                      {loading[5] ? 'Loading...' : 'Buy 5 Credits'}
                    </button>
                  </div>

                  {/* 10 Credits Option - Best Value */}
                  <div className="border border-primary rounded-3 p-3 position-relative" style={{ borderWidth: '2px' }}>
                    <div className="position-absolute top-0 end-0 m-2">
                      <span className="badge bg-success text-uppercase px-2 py-1" style={{ fontSize: '11px' }}>
                        Best Value
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ marginTop: '24px' }}>
                      <div>
                        <h4 
                          className="fw-bold mb-1"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '18px',
                            color: '#181a25'
                          }}
                        >
                          10 Credits
                        </h4>
                        <p 
                          className="text-muted mb-0 small"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '14px'
                          }}
                        >
                          Resume + Interview Prep for 10 jobs
                        </p>
                        <p 
                          className="text-success mb-0 small fw-bold mt-1"
                          style={{
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                            fontSize: '12px'
                          }}
                        >
                          Save $15 (30% off)
                        </p>
                      </div>
                      <div className="text-end">
                        <div 
                          className="fw-bold"
                          style={{
                            fontSize: '24px',
                            color: '#505fe2',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                          }}
                        >
                          $35 <span style={{ fontSize: '14px' }}>USD</span>
                        </div>
                        <div 
                          className="text-muted small text-decoration-line-through"
                          style={{ fontSize: '14px' }}
                        >
                          $50 <span style={{ fontSize: '10px' }}>USD</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleBuyCredits(10)}
                      className="btn btn-primary btn-sm w-100"
                      disabled={loading[10]}
                    >
                      {loading[10] ? 'Loading...' : 'Buy 10 Credits'}
                    </button>
                  </div>

                  <p 
                    className="text-muted small text-center mt-3 mb-0"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '12px'
                    }}
                  >
                    Credits never expire. Use them whenever you need.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ or Additional Info */}
            <div className="row mt-4">
              <div className="col-lg-8 mx-auto">
                <div className="text-center">
                  <p 
                    className="text-muted mb-0"
                    style={{
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                      fontSize: '14px'
                    }}
                  >
                    <strong>What's included in each credit?</strong> One complete job application package: 
                    AI-analyzed job posting, tailored resume, and personalized interview prep plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Pricing

